import { cx } from '@emotion/css';
import { CategoryOptInCookie } from '@snapchat/mw-cookie-components';
import { type FormBody, Alignment, Form } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useCallback, useContext, useMemo } from 'react';

import { AppContext } from '../../../../AppContext';
import type { FormResponseWithUrl } from '../../../../components/Form';
import { ElementLocation } from '../../../../context/ElementLocationContext/ElementLocationContext';
import { logError, logUserEvent } from '../../../../helpers/logging';
import { UserAction } from '../../../../types/events';
import { makePostSubmitUrlWithDownloadId, persistDownloadId } from '../../helpers';
import type { PlatformString } from '../../server';
import { alignCenterCss, alignRightCss, formCss } from './ArDownloadFormBlock.styles';
import { makeArDownloadFormFields } from './makeArDownloadFormFields';
import type { ArDownloadFormProps } from './types';
import { ArDownloadFormStyle } from './types';

export const ArDownloadForm: FC<ArDownloadFormProps> = ({
  id,
  endpoint,
  version,
  eula,
  platform,
  analytics,
  submitText,
  submitSuccessText,
  alignment = Alignment.Left,
  formStyle = ArDownloadFormStyle.Block,
  useCompactText = false,
  onDownloadSuccess,
}) => {
  // use Memo is needed to prevent main Form component to rerenreder
  const ArFormFields = useMemo(
    () =>
      makeArDownloadFormFields({
        version,
        eula,
        platform,
        formStyle,
        alignment,
        submitText,
        submitSuccessText,
        useCompactText,
      }),
    [version, eula, platform, formStyle, alignment, submitText, submitSuccessText, useCompactText]
  );

  const elementLocation = useContext(ElementLocation).elementLocation;
  const { userLocation, currentLocale, cookieManager } = useContext(AppContext);
  const country = userLocation.country;

  const onSubmitSuccess = useCallback(
    async (response: Response): Promise<void> => {
      if (analytics) {
        logUserEvent({
          eventCategory: 'ArDownloadForm',
          eventAction: UserAction.FormSubmit,
          eventLabel: analytics.label ?? id,
          context: {
            elementLocation,
          },
        });
      }

      try {
        const downloadFormResponse = (await response.json()) as FormResponseWithUrl & {
          version: string;
          platform: PlatformString;
          downloadId: string;
        };

        const { url, downloadId, platform, version } = downloadFormResponse;

        const acceptsMarketing =
          cookieManager.getCookie(CategoryOptInCookie.Essential) === 'true' &&
          cookieManager.getCookie(CategoryOptInCookie.Marketing) === 'true';

        if (acceptsMarketing) {
          persistDownloadId({ version, platform, downloadId }, cookieManager);
          const newurl = makePostSubmitUrlWithDownloadId({ version, platform, downloadId });
          window.history.pushState({ path: newurl }, '', newurl);

          if (analytics) {
            logUserEvent({
              eventCategory: 'ArDownloadForm',
              eventAction: UserAction.FormSubmit,
              eventLabel: 'lens_studio_post_submit',
              context: {
                elementLocation,
              },
            });
          }
        }

        onDownloadSuccess(url);
      } catch (_err) {
        // reset to default
        onDownloadSuccess(undefined);
      }
    },
    [analytics, elementLocation, id, cookieManager]
  );

  const onSubmitFailure = (error: Error, formBody: FormBody) => {
    logError({
      component: 'ArDownloadForm',
      error,
      message: `AR Download Form submit failed. Payload: ${JSON.stringify(formBody)}`,
      action: 'Submit',
    });
  };

  const extraParams = useMemo(() => {
    return { locale: currentLocale, country };
  }, [currentLocale, country]);

  return (
    <>
      <Form
        className={cx(formCss, {
          [alignRightCss]: alignment === Alignment.Right,
          [alignCenterCss]: alignment === Alignment.Center,
        })}
        endpoint={endpoint}
        formChildrenRenderer={ArFormFields}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmitSuccess={onSubmitSuccess}
        onSubmitFailure={onSubmitFailure}
        extraParams={extraParams}
      />
    </>
  );
};
